/**
 * Moves an element from indexA to indexB in an array.
 * @param arr - The array containing elements.
 * @param indexA - The index of the element to move.
 * @param indexB - The index where the element should be moved.
 * @returns - The modified array with the element moved.
 */
export function moveElement<T>(arr: T[], indexA: number, indexB: number): T[] {
  const newArray = [...arr];
  // Remove the element from indexA
  const [element] = newArray.splice(indexA, 1);

  if (!element) {
    return arr;
  }

  // Insert the element at indexB
  newArray.splice(indexB, 0, element);

  return newArray;
}

/**
 * concat two array while merging element of array1 with element of array2 based on a given property
 * @param array1
 * @param array2
 * @param key
 * @returns
 */
export function mergeElements<T>(array1: T[], array2: T[], key: keyof T): T[] {
  const map = new Map(array1.map((x) => [x[key], x]));
  array2.forEach((element) => {
    const mapKey = element[key];
    map.set(mapKey, { ...map.get(mapKey), ...element });
  });
  return map.values().toArray();
}

export const uniq = <TItem>(arr: TItem[]): TItem[] => {
  return Array.from(new Set(arr));
};

export const groupBy = <TItem>(
  allItems: TItem[] | readonly TItem[],
  groupFn: (item: TItem) => string,
): Record<string, TItem[]> => {
  // why cast to TItem[] ? because without it, TS infers acc as TItem instead of Record<string, TItem[]>
  return (allItems as TItem[]).reduce(
    (acc, item) => {
      const key = groupFn(item);
      let array = acc[key];

      if (array === undefined) {
        array = [] as TItem[];
        acc[key] = array;
      }

      array.push(item);

      return acc;
    },
    {} as Record<string, TItem[]>,
  );
};

export const createRecord = <TItem>(
  allItems: TItem[] | readonly TItem[],
  groupFn: (item: TItem) => string,
): Record<string, TItem> => {
  // why cast to TItem[] ? because without it, TS infers acc as TItem instead of Record<string, TItem>
  return (allItems as TItem[]).reduce(
    (acc, item) => {
      const key = groupFn(item);

      acc[key] = item;

      return acc;
    },
    {} as Record<string, TItem>,
  );
};
